import type { ForexOperationsOverviewAgentPage, ForexOperationsOverviewCore, ForexOperationsOverviewEquity, ForexOperationsOverviewRuntime, ForexOperationsOverviewPerformance, ForexOperationsOverviewFollowObserved, ForexOperationsOverviewDecisionObserved, ForexOperationsOverviewMarketObserved, ForexOperationsOverviewExecutionObserved, ForexOperatorTodoPage } from "@/lib/forex-api";
import type { ForexOperationsOverviewModel, OverviewMetric, OverviewMetricKey, OverviewRange, OverviewSectionState } from "./types";

const explanations: Record<OverviewMetricKey, string> = {
  users: "Forex 身份库中完成注册的去重用户总数；历史索引未核对时不展示。",
  followers: "当前有已生效、运行中且同步完成的跟随关系的去重用户数。",
  accounts: "账号状态为 linked 的跟随账号总数；不代表当前在线或可交易。",
  agents: "当前生命周期可用且已上架的 Agent 数量；不等于交易就绪数。",
  equity: "当前有效跟随关系关联的物理 Broker 账号权益；不是策略管理资产。只汇总身份、环境、币种与时效已核验的心跳快照，重复平台账号只计一次。",
  fills: "当前范围数据库已观测开/平订单；只有来源核验后才显示已知部分，不代表完整 Broker 成交。",
  realized_pnl: "已实现净盈亏：费用与成交覆盖合同尚未接入。",
  floating_pnl: "当前可归因持仓浮盈：持仓覆盖合同尚未接入。",
};
const unavailableSupport: Partial<Record<OverviewMetricKey, string>> = {
  users: "身份索引待完成历史核对", accounts: "账号索引待完成历史核对", equity: "快照覆盖待核验",
  fills: "数据库已观测订单待读取", realized_pnl: "费用与历史覆盖待核验", floating_pnl: "持仓归属与快照待核验",
};

const taskKinds = [
  ["agent_review", "Agent 待审核", "agent-reviews"],
  ["follower_review", "子账号待审核", "follower-reviews"],
  ["follower_material", "子账号待补材料", "follower-reviews"],
  ["follower_provision_blocked", "子账号开通受阻", "follower-reviews"],
] as const;
const anomalyKinds = [
  ["execution_unknown", "执行结果待核对", "account-commands"],
  ["instance_maintenance", "实例维护未完成", "terminals"],
] as const;

const runtimeCoverage = (items: Array<{ id: string }>) => {
  const kinds = new Set(items.map((item) => item.id));
  const observed = [kinds.has("market") && "平台行情", kinds.has("decision") && "Demo 决策", kinds.has("execution") && "执行命令"]
    .filter(Boolean).join("、");
  const terminal = kinds.has("terminal") ? "Terminal/EA 为账号范围内的心跳观察" : "Terminal/EA 状态未取得";
  return `${terminal}${observed ? `；${observed}仅为局部诊断` : ""}；全域健康待核验。`;
};

const runtimeReasonLabels: Record<string, string> = {
  ACCOUNT_OWNER_UNKNOWN: "账号归属未核验",
  ACCOUNT_IDENTITY_UNVERIFIED: "账号与心跳身份未核验",
  ACCOUNT_ENVIRONMENT_UNKNOWN: "Demo/Live 环境未知",
  TERMINAL_STALE: "Terminal 心跳已过期",
  TERMINAL_OFFLINE: "Terminal 连接离线",
  TERMINAL_HEARTBEAT_UNKNOWN: "Terminal 心跳缺失或无效",
  APPLICABLE_AGENT_COVERAGE_UNVERIFIED: "尚未证明覆盖全部适用 Agent",
  EXPECTED_RUN_COVERAGE_UNVERIFIED: "尚未核对预期决策调度轮次",
  LEGACY_OR_OTHER_EXECUTION_MODES_EXCLUDED: "不含旧版及其他执行模式",
  NO_SIGNAL_NOT_EQUIVALENT_TO_HOLD: "无信号不能等同于 HOLD",
  EXPECTED_COMMAND_COVERAGE_UNVERIFIED: "尚未核对预期执行命令全集",
  EA_RESULT_NOT_BROKER_FILL: "EA 回报不能等同于 Broker 成交",
};
const observedReasons = (codes: string[]) => codes.map((code) => ({
  code, label: runtimeReasonLabels[code] ?? code,
}));

export function operationsOverviewPlaceholder(
  state: Extract<OverviewSectionState, "loading" | "error" | "offline" | "forbidden">,
  range: OverviewRange,
  environmentLabel: string,
): ForexOperationsOverviewModel {
  const section = { state, data: null };
  return {
    environmentLabel, accountScopeLabel: "当前服务环境 · 跟随业务（账号环境未分组）", timeZone: "Asia/Shanghai", generatedAt: null,
    range, currency: "币种待核验", metrics: (["users", "followers", "accounts", "agents", "equity", "fills", "realized_pnl", "floating_pnl"] as OverviewMetricKey[])
      .map((key) => ({ key, value: null, state, explanation: explanations[key] })),
    userTrend: section, tradeTrend: section, runtime: section, anomalies: section, tasks: section, agents: section,
  };
}

/** Only current, source-backed facts enter the live model. Other cells remain unavailable. */
export function operationsOverviewFromCore(
  core: ForexOperationsOverviewCore,
  range: OverviewRange,
  environmentLabel: string,
  agentPage?: ForexOperationsOverviewAgentPage | null,
  todoPage?: ForexOperatorTodoPage | null,
): ForexOperationsOverviewModel {
  const formatAsOf = (stamp: number) => new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(stamp * 1000));
  const asOf = formatAsOf(core.generated_at);
  const values: Partial<Record<OverviewMetricKey, number>> = { followers: core.follower_users, agents: core.listed_agents };
  if (core.registered_users !== null && core.registered_users !== undefined) values.users = core.registered_users;
  if (core.linked_follower_accounts !== null && core.linked_follower_accounts !== undefined) values.accounts = core.linked_follower_accounts;
  const keys: OverviewMetricKey[] = ["users", "followers", "accounts", "agents", "equity", "fills", "realized_pnl", "floating_pnl"];
  const metrics: OverviewMetric[] = keys.map((key) => ({
    key, value: values[key] === undefined ? null : values[key]!.toLocaleString("en-US"),
    state: values[key] === undefined ? "unavailable" : "ready",
    support: key === "followers" ? `有效关系 ${core.active_relations.toLocaleString("en-US")} · 授权账号 ${core.authorized_accounts.toLocaleString("en-US")}（非已接入）` : key === "users" && values.users !== undefined ? "全站注册身份 · 历史索引已核对" : key === "accounts" && values.accounts !== undefined ? "状态 linked · 在线与交易就绪待核验" : unavailableSupport[key],
    asOf: values[key] === undefined ? undefined : asOf,
    explanation: explanations[key],
  }));
  const itemsFor = (kinds: ReadonlyArray<readonly [string, string, string]>) => kinds.flatMap(([kind, title, target]) => {
    const count = (todoPage?.counts.categories ?? core.attention.categories)[kind]?.count;
    return count === undefined || count <= 0 ? [] : [{ id: kind, title, detail: `${count} 项`, severity: "ungraded" as const, target }];
  });
  const rowsFor = (kinds: ReadonlyArray<readonly [string, string, string]>) => {
    if (!todoPage) return itemsFor(kinds);
    const rows = todoPage.items.filter((item) => kinds.some(([kind]) => kind === item.kind))
      .map((item) => ({ id: item.id, title: item.title, detail: "查看来源详情",
        since: formatAsOf(item.updated_at),
        severity: "ungraded" as const, target: kinds.find(([kind]) => kind === item.kind)?.[2] }));
    const missingKinds = kinds.filter(([kind]) => !rows.some((row) => todoPage.items.some((item) => item.id === row.id && item.kind === kind)));
    return [...rows, ...itemsFor(missingKinds)];
  };
  const tasks = rowsFor(taskKinds);
  const anomalies = rowsFor(anomalyKinds);
  const countFor = (kinds: ReadonlyArray<readonly [string, string, string]>) => kinds.reduce(
    (sum, [kind]) => sum + ((todoPage?.counts.categories ?? core.attention.categories)[kind]?.count ?? 0), 0);
  const attentionDetails = [
    ["agent_review", "Agent 审核"], ["follower_review", "子账号审核"],
    ["follower_material", "待补材料"], ["follower_provision_blocked", "开通受阻"],
    ["execution_unknown", "执行待核对"], ["instance_maintenance", "实例维护"],
  ].flatMap(([kind, label]) => {
    const count = core.attention.categories[kind]?.count;
    return count && count > 0 ? [`${label} ${count}`] : [];
  });
  const registration = core.registration_trend;
  const trendDays = range === "today" ? 1 : range === "7d" ? 7 : 30;
  const trendPoints = registration?.points.slice(-trendDays) ?? [];
  const coverageStart = registration ? new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(registration.coverage_started_at_ms)) : null;
  const userTrend = registration && trendPoints.length === trendDays ? {
    state: trendPoints.some((point) => point.coverage !== "full") ? "partial" as const : "ready" as const,
    data: { title: "新增注册用户", kind: "bar" as const, primaryLabel: "新增注册", points: trendPoints.map((point) => ({
      date: point.date, label: point.date.slice(5).replace("-", "/"), primary: point.count,
    })) },
    coverage: `注册完成时间自 ${coverageStart} 起有完整索引；此前日期及首次跟随趋势暂不可用`,
    asOf,
  } : { state: "unavailable" as const, data: null, reason: "注册完成时间索引待核对；首次跟随历史尚未接入" };
  const tradeTrend = { state: "unavailable" as const, data: null, reason: "Broker 成交时间与期间收益尚未完成对账" };
  const runtime = { state: "unavailable" as const, data: null, reason: "行情、决策、执行和终端健康尚未形成统一投影" };
  const agentCountChanged = agentPage && agentPage.total !== core.listed_agents;
  const agents = agentPage ? { state: agentPage.items.length ? "partial" as const : "empty" as const,
    totalCount: agentPage.total,
    data: agentPage.items.map((item) => ({ id: item.agent_id, name: item.name, symbols: item.configured_symbols ?? [],
      runtimeLabel: item.desired_state === "running" ? "期望运行" : item.desired_state === "stopped" ? "已要求停止" : "控制状态待核验",
      readinessLabel: "实际运行 / 就绪待核验",
      followers: item.follower_users.toLocaleString("en-US"), relations: item.active_relations.toLocaleString("en-US"),
      decisions: agentPage.decision_observation_state === "partial" && item.observed_demo_decisions_24h != null
        ? item.observed_demo_decisions_24h === 0 ? "无记录" : `已观测 ${item.observed_demo_decisions_24h}` : null,
      masterFills: null, followerFills: null, realizedPnl: null, latestIssue: null })),
    coverage: `已上架 Agent ${agentPage.total} 个；决策仅为平台近 24 小时已记录轮次，预期调度未核验；品种来自生效配置，运行字段仅为控制意图，交易与收益待接入${agentCountChanged ? "；摘要与列表数量不同，请刷新核对" : ""}`, asOf: formatAsOf(agentPage.generated_at) }
    : { state: "unavailable" as const, data: null, reason: "Agent 列表来源暂不可用" };
  return {
    environmentLabel, accountScopeLabel: "当前服务环境 · 跟随业务（账号环境未分组）", timeZone: "Asia/Shanghai", generatedAt: asOf,
    range, currency: "币种待核验", metrics,
    briefing: { state: "ready", data: { headline: core.attention.total > 0 ? `${core.attention.total} 项需关注` : "当前无已覆盖待办", detail: attentionDetails.length ? `${attentionDetails.join(" · ")}；仅覆盖审核、补材料、开通受阻、未知执行和实例维护` : "仅覆盖审核、补材料、开通受阻、未知执行和实例维护，不代表全部运行健康", health: core.attention.total > 0 ? "attention" : "unknown" } },
    userTrend, tradeTrend, runtime,
    anomalies: { state: anomalies.length ? "ready" : "empty", data: anomalies, totalCount: countFor(anomalyKinds), asOf: todoPage ? formatAsOf(todoPage.generated_at) : asOf },
    tasks: { state: tasks.length ? "ready" : "empty", data: tasks, totalCount: countFor(taskKinds), asOf: todoPage ? formatAsOf(todoPage.generated_at) : asOf }, agents,
  };
}

const moneyText = (value: string) => {
  const [whole, fraction] = value.split(".");
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${fraction === undefined ? "" : `.${fraction}`}`;
};

const equityReasonLabels: Record<string, string> = {
  ACCOUNT_OWNER_UNKNOWN: "账号归属未核验",
  ACCOUNT_IDENTITY_UNVERIFIED: "绑定或心跳身份不一致",
  ACCOUNT_ENVIRONMENT_UNKNOWN: "Demo/Live 身份未知",
  PHYSICAL_IDENTITY_MISSING: "物理账号去重身份缺失",
  PHYSICAL_IDENTITY_KEY_MIXED: "账号身份密钥版本混用",
  PHYSICAL_ACCOUNT_CONFLICT: "同一物理账号快照冲突",
  ACCOUNT_EQUITY_MISSING: "权益或币种快照缺失",
};

export function operationsOverviewWithEquity(
  model: ForexOperationsOverviewModel,
  equity: ForexOperationsOverviewEquity | null,
  environment: "simulation" | "real",
  selectedCurrency?: string,
): ForexOperationsOverviewModel {
  const accountScopeLabel = `规模为当前服务环境全量 · 权益为有效跟随账号 ${environment === "simulation" ? "Demo" : "Live"}`;
  const valid = equity?.account_environment === environment &&
    equity.scope_hash === `forex-admin:follower:${environment}`;
  const currencies = valid ? equity.currencies.map((item) => item.currency) : [];
  const chosen = currencies.includes(selectedCurrency ?? "") ? selectedCurrency : currencies[0];
  const item = valid ? equity.currencies.find((candidate) => candidate.currency === chosen) : undefined;
  const unknown = valid ? equity.unknown_platform_accounts : 0;
  const display = item ? `${item.state === "partial" ? "已知 " : ""}${moneyText(item.known_value)}` : null;
  const state: OverviewSectionState = item ? item.state : valid && equity.target_platform_accounts === equity.excluded_platform_accounts && unknown === 0 ? "empty" : "unavailable";
  const support = item
    ? `物理账号 ${item.covered_physical_accounts} 个 · 已核验平台记录 ${item.covered_platform_accounts} 条${unknown ? ` · 未核验 ${unknown} 条` : ""}`
    : valid && unknown ? `${unknown} 条账号记录未核验，无法确定完整权益` : valid ? "当前范围无已核验账号权益" : "权益来源暂不可用";
  const qualityReasons = valid ? Object.entries(equity.reason_counts)
    .filter(([, count]) => count > 0)
    .map(([code, count]) => ({ code, count, label: equityReasonLabels[code] ?? code })) : undefined;
  const metrics = model.metrics.map((metric) => metric.key === "equity" ? {
    ...metric, value: display, unit: item?.currency, state, support,
    coverage: valid ? `${support}；当前有效授权平台账号 ${equity.target_platform_accounts} 条，其他账号环境 ${equity.excluded_platform_accounts} 条` : support,
    qualityReasons,
    snapshotHash: valid && /^[0-9a-f]{64}$/.test(equity.snapshot_hash ?? "") ? equity.snapshot_hash ?? undefined : undefined,
    qualityTotal: valid ? equity.unknown_platform_accounts : undefined,
    asOf: item ? new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(item.observed_at * 1000)) : undefined,
  } : metric);
  return { ...model, accountScopeLabel, accountEnvironment: environment,
    availableCurrencies: currencies, currency: chosen ?? "币种待核验", metrics };
}

export function operationsOverviewWithRuntime(
  model: ForexOperationsOverviewModel,
  runtime: ForexOperationsOverviewRuntime | null,
  environment: "simulation" | "real",
): ForexOperationsOverviewModel {
  if (!runtime || runtime.account_environment !== environment ||
      runtime.scope_hash !== `forex-admin:follower:${environment}`) return model;
  const terminal = runtime.terminal;
  const label = environment === "simulation" ? "Demo" : "Live";
  const terminalSummary = terminal.state === "empty" && terminal.unknown_environment_count === 0
    ? `${label} 当前无适用的有效跟随账号`
    : `${label} 在线 ${terminal.online_count} / 已识别 ${terminal.applicable_platform_accounts} · 过期 ${terminal.stale_count} · 离线 ${terminal.offline_count} · 未知 ${terminal.unknown_count + terminal.unknown_environment_count}`;
  const asOf = new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(runtime.evaluated_at * 1000));
  const unknown = [
    { id: "market", label: "行情来源", summary: "适用对象与时效覆盖待核验", health: "unknown" as const,
      scope: "Agent 适用行情范围待核验", explanation: "平台行情来源尚未与 Agent 生效品种和交易时段形成完整覆盖对应。" },
    { id: "decision", label: "决策调度", summary: "逻辑轮次与 HOLD 分类待核验", health: "unknown" as const,
      scope: "决策调度范围待核验", explanation: "预期调度与实际逻辑轮次尚未逐项对账，不能把无记录视为正常。" },
    { id: "execution", label: "执行与回报", summary: "命令、回执和 Broker 结果覆盖待核验", health: "unknown" as const,
      scope: "命令、EA 回报与 Broker 结果范围待核验", explanation: "命令与 EA 回报不能替代 Broker 成交结果。" },
  ].filter((item) => runtime.unavailable_domains.includes(item.id as "market" | "decision" | "execution"));
  const items = [{ id: "terminal", label: "Terminal / EA 心跳", summary: terminalSummary,
    health: terminal.state === "ready" ? terminal.health : "unknown" as const,
    scope: `有效跟随账号 · ${label}`,
    explanation: `仅评价当前账号范围内的 Terminal/EA 心跳；过期阈值 ${terminal.stale_after_seconds} 秒。未知环境 ${terminal.unknown_environment_count} 条、其他账号环境 ${terminal.excluded_environment_count} 条不计入 ${label} 在线率。在线不代表可交易或 Broker 结果已确认。`,
    qualityReasons: Object.entries(terminal.reason_counts).filter(([, count]) => count > 0)
      .map(([code, count]) => ({ code, label: runtimeReasonLabels[code] ?? code, count })),
  }, ...unknown];
  return { ...model, runtime: { state: "partial", data: items, asOf,
    coverage: runtimeCoverage(items) } };
}

/** Recorded platform Demo runs are a diagnostic, not account-scoped health. */
export function operationsOverviewWithDecisionObserved(
  model: ForexOperationsOverviewModel,
  decision: ForexOperationsOverviewDecisionObserved | null,
): ForexOperationsOverviewModel {
  if (!decision || decision.schema_version !== "OperationsOverviewDecisionObservedV1" ||
      !decision.scope_hash || decision.source !== "forex_master_signal_runs" ||
      decision.source_environment !== "demo" || decision.history_status !== "observed_only") return model;
  const counts = decision.status_counts;
  const summary = decision.state === "partial"
    ? `近 24 小时创建轮次 ${decision.total_observed} · 生成信号 ${counts.signal_generated ?? 0} · 无信号 ${counts.no_signal ?? 0} · 失败 ${counts.failed ?? 0} · 阻断 ${counts.blocked ?? 0}；无信号不等于 HOLD`
    : "近 24 小时未记录轮次；预期调度覆盖待核验";
  const row = { id: "decision", label: "决策轮次（平台 Demo）", summary, health: "unknown" as const,
    scope: "平台 Demo · 近 24 小时创建的逻辑轮次",
    explanation: "只统计已记录的逻辑决策轮次，不含未触发调度与其他执行模式；无信号不等于 HOLD，失败数也不证明全域健康。",
    qualityReasons: observedReasons(decision.reason_codes),
    asOf: decision.latest_updated_at ? new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(decision.latest_updated_at * 1000)) : undefined };
  const prior = model.runtime.data ?? [];
  const data = prior.some((item) => item.id === "decision")
    ? prior.map((item) => item.id === "decision" ? row : item) : [...prior, row];
  return { ...model, runtime: { state: "partial", data, asOf: model.runtime.asOf,
    coverage: runtimeCoverage(data) } };
}

/** Platform collector observations do not establish per-Agent market coverage. */
export function operationsOverviewWithMarketObserved(
  model: ForexOperationsOverviewModel,
  market: ForexOperationsOverviewMarketObserved | null,
): ForexOperationsOverviewModel {
  if (!market || market.schema_version !== "OperationsOverviewMarketObservedV1" ||
      !market.scope_hash || market.source !== "platform_collector") return model;
  const sourceStatus = { fresh: "源状态新鲜", stale: "源状态过期", paused: "源已暂停", unavailable: "源状态未知" }[market.status];
  const summary = market.state === "partial"
    ? `${sourceStatus} · 已配置 ${market.symbol_count} 品种 · 新鲜 ${market.fresh_symbol_count} · 过期/未确认 ${market.stale_symbol_count}；Agent 适用覆盖未核验`
    : "平台 Collector 来源暂无可确认观测";
  const row = { id: "market", label: "行情来源（平台）", summary, health: "unknown" as const,
    scope: `平台 Collector · 路由版本 ${market.route_revision}`,
    explanation: "平台行情源只反映已配置品种的最近接收状态；尚未将 Agent 生效品种、适用市场与交易时段逐项核对，不能推断每个 Agent 的行情健康。",
    qualityReasons: observedReasons(market.reason_codes),
    asOf: market.last_received_at ? new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(market.last_received_at * 1000)) : undefined };
  const prior = model.runtime.data ?? [];
  const data = prior.some((item) => item.id === "market")
    ? prior.map((item) => item.id === "market" ? row : item) : [...prior, row];
  return { ...model, runtime: { ...model.runtime, state: "partial", data,
    coverage: runtimeCoverage(data) } };
}

/** EA command states are not Broker fills and span both account environments. */
export function operationsOverviewWithExecutionObserved(
  model: ForexOperationsOverviewModel,
  execution: ForexOperationsOverviewExecutionObserved | null,
): ForexOperationsOverviewModel {
  if (!execution || execution.schema_version !== "OperationsOverviewExecutionObservedV1" ||
      !execution.scope_hash || execution.source !== "forex_master_execution_commands" ||
      execution.source_scope !== "all_account_environments") return model;
  const counts = execution.status_counts;
  const summary = execution.state === "partial"
    ? `近 24 小时创建命令 ${execution.total_observed} · EA 已回报执行结果 ${(counts.filled ?? 0) + (counts.applied ?? 0)} · 结果未知 ${counts.unknown ?? 0}；命令回报不等于 Broker 成交`
    : "近 24 小时未记录账号执行命令；预期覆盖待核验";
  const row = { id: "execution", label: "执行命令（全部账号环境）", summary,
    health: "unknown" as const,
    scope: "全部账号环境 · 近 24 小时创建的账号命令",
    explanation: "这里观察账号执行命令及 EA 回报状态，未按页面 Demo/Live 筛选；命令已回报不等于 Broker 已成交，未知结果仍需独立核对。",
    qualityReasons: observedReasons(execution.reason_codes),
    asOf: execution.latest_updated_at ? new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(execution.latest_updated_at * 1000)) : undefined };
  const prior = model.runtime.data ?? [];
  const data = prior.some((item) => item.id === "execution")
    ? prior.map((item) => item.id === "execution" ? row : item) : [...prior, row];
  return { ...model, runtime: { ...model.runtime, state: "partial", data,
    coverage: runtimeCoverage(data) } };
}

export function operationsOverviewWithPerformance(
  model: ForexOperationsOverviewModel,
  performance: ForexOperationsOverviewPerformance | null,
  environment: "simulation" | "real",
  range: OverviewRange,
): ForexOperationsOverviewModel {
  if (!performance || !performance.scope_hash || performance.schema_version !== "OperationsOverviewPerformanceV1" ||
      performance.account_role !== "follower" || performance.account_environment !== environment ||
      performance.range !== range || performance.currency !== (model.availableCurrencies?.length ? model.currency : null) ||
      performance.timezone !== "Asia/Shanghai" || performance.fills.value !== null ||
      performance.fills.history_status !== "unverified") return model;
  const fills = performance.fills;
  const observed = fills.known_opened + fills.known_closed;
  if (!Number.isSafeInteger(observed) || observed < 0 ||
      fills.state !== (observed ? "partial" : "unavailable")) return model;
  const support = observed
    ? `数据库已记录 · 开仓 ${fills.known_opened} / 平仓 ${fills.known_closed}；归属与历史完整性未核验`
    : "数据库当前无可核验的开/平订单；不能据此判定该期间成交为 0";
  const qualityReasons = [
    ["BROKER_ECONOMIC_EVENT_CONFLICT", "经济事件身份冲突", fills.conflicting_keys],
    ["BROKER_ACCOUNT_IDENTITY_UNKNOWN", "物理账号身份未知", fills.unknown_identity_events],
    ["BROKER_TIME_UNVERIFIED", "Broker 时间未核验", fills.unknown_time_events],
  ].filter(([, , count]) => Number(count) > 0)
    .map(([code, label, count]) => ({ code: String(code), label: String(label), count: Number(count) }));
  const dayCount = range === "today" ? 1 : range === "7d" ? 7 : 30;
  const startDate = new Date((performance.from_utc + 8 * 3600) * 1000).toISOString().slice(0, 10);
  const startTime = Date.parse(`${startDate}T00:00:00Z`);
  const daily = fills.observed_daily;
  const dailyMatches = Array.isArray(daily) && daily.length === dayCount && daily.every((point, index) =>
    point.date === new Date(startTime + index * 86400_000).toISOString().slice(0, 10) &&
    [point.known_opened, point.known_closed].every((count) => count === null ||
      (Number.isSafeInteger(count) && count > 0))) &&
    daily.reduce((sum, point) => sum + (point.known_opened ?? 0), 0) === fills.known_opened &&
    daily.reduce((sum, point) => sum + (point.known_closed ?? 0), 0) === fills.known_closed;
  if (!dailyMatches) return model;
  const tradeTrend = observed ? {
    state: "partial" as const,
    data: { title: "数据库已观测开/平订单", kind: "bar" as const,
      primaryLabel: "已观测开仓", secondaryLabel: "已观测平仓",
      points: daily.map((point) => ({ date: point.date, label: point.date.slice(5).replace("-", "/"),
        primary: point.known_opened, secondary: point.known_closed })) },
    coverage: "仅当前数据库已观测记录；历史完整性和 ForexMind 归属未核验，无记录日留空",
    asOf: new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(performance.evaluated_at * 1000)),
  } : model.tradeTrend;
  return { ...model, tradeTrend, metrics: model.metrics.map((metric) => metric.key === "fills" ? {
    ...metric, value: observed ? String(observed) : null, state: observed ? "partial" : "unavailable", support,
    coverage: `${support} · 重复传输 ${fills.duplicate_transmissions} 条已折叠`, qualityReasons,
    asOf: new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(performance.evaluated_at * 1000)),
    explanation: "按物理账号、票号、时间和经济字段折叠重复传输的数据库已观测开/平订单；其中可能包含尚未归因于 ForexMind 的交易。源端历史范围与部分平仓身份未证明完整，此数不能作为全部 Broker 成交次数。",
  } : metric) };
}

export function operationsOverviewWithFollowObserved(
  model: ForexOperationsOverviewModel,
  observed: ForexOperationsOverviewFollowObserved | null,
  range: OverviewRange,
): ForexOperationsOverviewModel {
  const count = range === "today" ? 1 : range === "7d" ? 7 : 30;
  if (!observed || observed.schema_version !== "OperationsOverviewFollowObservedV1" ||
      !observed.scope_hash || observed.range !== range || observed.timezone !== "Asia/Shanghai" ||
      observed.history_status !== "unverified" || observed.label !== "first_observed_follow" ||
      observed.points.length !== count || observed.points.some((point) =>
        !/^\d{4}-\d{2}-\d{2}$/.test(point.date) ||
        (point.count !== null && (!Number.isSafeInteger(point.count) || point.count < 1))) ||
      new Set(observed.points.map((point) => point.date)).size !== count) return model;
  const existing = model.userTrend.data;
  if (existing && existing.points.map((point) => point.date).join(",") !== observed.points.map((point) => point.date).join(",")) return model;
  const points = existing ? existing.points.map((point, index) => ({
    ...point, secondary: observed.points[index].count,
  })) : observed.points.map((point) => ({
    date: point.date, label: point.date.slice(5).replace("-", "/"), primary: point.count,
  }));
  const trend = existing ? { ...existing, secondaryLabel: "首次已观测跟随（非历史首次）", points }
    : { title: "用户与跟随增长", kind: "bar" as const,
      primaryLabel: "首次已观测跟随（非历史首次）", points };
  const coverage = "全站范围，不按 Demo/Live 分组；跟随仅为已观测运行生效，历史首次未核验，缺日留空";
  return { ...model, userTrend: {
    state: "partial", data: trend,
    asOf: new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(observed.evaluated_at * 1000)),
    coverage: existing ? `${model.userTrend.coverage ?? ""}；${coverage}` : coverage,
  } };
}
